import './styles.scoped.css'
import {useEffect} from 'react'
import {Typography, Button, IconButton, Modal, TextField, Avatar, Chip} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {Controller, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useMobile} from '../../hooks'
import {
    getColorFromName,
    getInitials,
    ignoreTimezone,
    parseError,
    formatTime,
    zonedWallTimeToUtcIso,
    utcIsoToZonedWallTime,
} from '../../utils/helpers'
import {completionNeedsReason, keepsRealClockIn, reportTimes} from '../../utils/visitActions'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {format, isValid} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {VisitDialogCardSkeleton} from '../Skeletons'

// The instant, in ms, that the typed wall-clock end time means on the visit's own date in the
// schedule's zone.
const endInstant = (date, time, zone) => Date.parse(zonedWallTimeToUtcIso(format(date, 'yyyy-MM-dd'), time, zone))

// The real clock-in of an in-progress visit as wall-clock "HH:mm" in the schedule's zone.
const clockInWallTime = item => utcIsoToZonedWallTime(item?.clock_in_time, item?.schedule?.timezone)

const completionNotice = item => {
    if (keepsRealClockIn(item)) {
        return `The caregiver clocked in at ${formatTime(clockInWallTime(item))} and has not clocked out. That clock-in stays as recorded. Completing the visit records the end time below as a manual EVV entry for the clock-out, using the reason below.`
    }
    if (item?.status === 'scheduled') {
        return 'This visit was never clocked in or out. Completing it records a manual EVV entry using the times and reason below.'
    }
    return 'This visit has no recorded clock-out. Completing it records a manual EVV entry using the times and reason below.'
}

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                items: yup.array().of(
                    yup.object().shape({
                        id: yup.string(),
                        status: yup.string(),
                        date: yup.date(),
                        start_time: yup.string().required('Start time is required'),
                        end_time: yup
                            .string()
                            .required('End time is required')
                            .test('after-start', 'End time must be after start time', function (value) {
                                if (!value) return true
                                const {start_time, date, clock_in_time, schedule} = this.parent
                                if (keepsRealClockIn(this.parent)) {
                                    // The caregiver's real clock-in stays, so the end must follow that instant.
                                    if (!isValid(date)) return true
                                    return (
                                        endInstant(date, value, schedule?.timezone) > Date.parse(clock_in_time) ||
                                        this.createError({message: 'End time must be after the clock-in time'})
                                    )
                                }
                                return !start_time || value.slice(0, 5) > start_time.slice(0, 5)
                            })
                            // The backend refuses an end time later than now, read in the schedule's zone.
                            .test('not-in-future', "End time can't be in the future", function (value) {
                                const {date, schedule} = this.parent
                                if (!value || !isValid(date)) return true
                                return endInstant(date, value, schedule?.timezone) <= Date.now()
                            }),
                        // Not named `reason`: a visit already carries its own `reason` field.
                        completion_reason: yup
                            .string()
                            .trim()
                            .max(500, 'Reason must be 500 characters or fewer')
                            .test('reason-required', 'A reason is required to record a manual EVV entry', function (value) {
                                return !completionNeedsReason(this.parent) || Boolean(value)
                            }),
                    }),
                ),
            }),
        ),
    })

    // Fill the form when the dialog opens, or when it is handed different visits. Not whenever
    // `items` changes: the parents pass a fresh array on every render (and render again on any
    // global state change, such as a toast clearing itself), which would wipe what was typed.
    const visitIds = items?.map(item => item?.id).join(',')
    useEffect(() => {
        if (open && items?.length > 0) {
            form.setValue(
                'items',
                items?.map(item => ({
                    ...item,
                    date: new Date(ignoreTimezone(item?.date)),
                    // The caregiver's real clock times, where there are any; the scheduled times otherwise.
                    // An in-progress visit's clock-in is kept whatever start time is sent, so it goes back as the start.
                    ...reportTimes(item),
                    completion_reason: '',
                })),
            )
        }
    }, [open, visitIds])

    const submit = data => {
        setLoading(true)
        scheduleApi
            .markAsComplete(branchId, items?.[0]?.schedule_id, {
                visits: data?.items?.map(item => {
                    const reason = item?.completion_reason?.trim()
                    return {
                        visit_id: item?.id,
                        // The date field is read-only: the backend only completes a visit on its own date.
                        date: format(new Date(item?.date), 'yyyy-MM-dd'),
                        start_time: item?.start_time?.slice(0, 5),
                        end_time: item?.end_time?.slice(0, 5),
                        ...(reason ? {reason} : {}),
                    }
                }),
            })
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Scheduled Marked as Complete!',
                        description: `The visit for ${items?.[0]?.schedule?.client?.first_name} on ${format(new Date(ignoreTimezone(items?.[0]?.date)), 'EEE, dd MMM yyyy')} has been successfully marked as complete.`,
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: `${items?.length} Scheduled Marked as Complete!`,
                        description: `The visit for ${items?.length} scheduled have been successfully marked as complete.`,
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Mark Schedule{items?.length > 1 ? 's' : ''} as Complete
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} disabled={loading} style={{flex: 1}}>
                                    Mark as Complete {items?.length > 1 ? `(${items?.length})` : ''}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Mark Schedule{items?.length > 1 ? 's' : ''} as Complete
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        You are about to finalize this schedule. Once completed, the status will be updated and no further changes can be made.
                    </Typography>
                    {items.map((item, index) =>
                        loading ? (
                            <div key={index}>
                                <br />
                                <VisitDialogCardSkeleton section="reportTime" />
                            </div>
                        ) : (
                            <div key={index}>
                                <br />
                                <div className="card">
                                    <div className="card-profile">
                                        <div className="people-card">
                                            {item?.schedule?.client?.avatar_url ? (
                                                <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(
                                                            item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                        ),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.schedule?.service?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                        {item?.status === 'scheduled' && (
                                            <Chip
                                                label="Scheduled"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: theme.palette.grey[700],
                                                    color: theme.palette.secondary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                        {item?.status === 'ended' && (
                                            <Chip
                                                label="Ended"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: '#02CAD1',
                                                    color: theme.palette.primary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                        {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                        {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                    </div>
                                    <br />
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                    <br />
                                    <Typography variant="h8" color="text.primary">
                                        Report Time
                                    </Typography>
                                    {completionNeedsReason(item) && (
                                        <>
                                            <br />
                                            <Typography variant="body3" color="text.secondary">
                                                {completionNotice(item)}
                                            </Typography>
                                        </>
                                    )}
                                    <br />
                                    <br />
                                    <Typography variant="inputLabel" color="text.secondary">
                                        Date
                                    </Typography>
                                    <Controller
                                        {...form.register(`items.${index}.date`)}
                                        control={form.control}
                                        render={({field}) => (
                                            <DatePicker
                                                placeholder="Enter your date"
                                                {...field}
                                                readOnly
                                                value={field.value ?? null}
                                                onChange={newValue => field.onChange(newValue)}
                                                enableAccessibleFieldDOMStructure={false}
                                                slots={{
                                                    textField: params => <TextField {...params} fullWidth size="small" />,
                                                }}
                                            />
                                        )}
                                    />
                                    <br />
                                    <br />
                                    <div className="form">
                                        <div className="form-left">
                                            {keepsRealClockIn(item) ? (
                                                <>
                                                    <Typography variant="inputLabel" color="text.secondary">
                                                        Clock-in Time
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={formatTime(clockInWallTime(item))}
                                                        slotProps={{input: {readOnly: true}, htmlInput: {'aria-label': 'Clock-in time'}}}
                                                        helperText="Recorded by the caregiver"
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="inputLabel" color="text.secondary">
                                                        Start Time
                                                    </Typography>
                                                    <TextField
                                                        type="time"
                                                        size="small"
                                                        fullWidth
                                                        {...form.register(`items.${index}.start_time`)}
                                                        error={Boolean(form.formState.errors.items?.[index]?.start_time)}
                                                        helperText={form.formState.errors.items?.[index]?.start_time?.message}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <div className="form-right">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                End Time
                                            </Typography>
                                            <TextField
                                                type="time"
                                                size="small"
                                                fullWidth
                                                {...form.register(`items.${index}.end_time`)}
                                                error={Boolean(form.formState.errors.items?.[index]?.end_time)}
                                                helperText={form.formState.errors.items?.[index]?.end_time?.message}
                                            />
                                        </div>
                                    </div>
                                    <br />
                                    <Typography variant="inputLabel" color="text.secondary">
                                        {completionNeedsReason(item) ? 'Reason' : 'Reason (optional)'}
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder={
                                            completionNeedsReason(item)
                                                ? 'Why is this visit being completed manually?'
                                                : 'Add a note about this completion'
                                        }
                                        slotProps={{htmlInput: {'aria-label': 'Reason'}}}
                                        {...form.register(`items.${index}.completion_reason`)}
                                        error={Boolean(form.formState.errors.items?.[index]?.completion_reason)}
                                        helperText={
                                            form.formState.errors.items?.[index]?.completion_reason?.message ||
                                            (completionNeedsReason(item) ? 'Required. Up to 500 characters.' : 'Up to 500 characters.')
                                        }
                                    />
                                    <br />
                                </div>
                            </div>
                        ),
                    )}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} disabled={loading}>
                                Mark as Complete {items?.length > 1 ? `(${items?.length})` : ''}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
